/**
 * Settings: her pots and asset register, clients, spots and requirements, the rules, and who's using
 * this phone. An iPhone-style grouped list; each item opens its own page. Every change confirms
 * in the white toast with Undo, then Redo. Editing never rewrites history (see catalogEdits).
 */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { ChevronLeft, ChevronRight, FileUp, Minus, Plus, Redo2, Share, TriangleAlert, Undo2, X } from "lucide-react";
import {
  addClient,
  addPot,
  addSlot,
  calendarDate,
  removeClient,
  removeSlot,
  updateClient,
  updatePot,
  updateSlot,
  validRequirement,
  type CalendarDate,
  type CatalogData,
  type Client,
  type EditResult,
  type History,
  type Pot,
  type Requirement,
  type Slot,
} from "@hitl/rotations-domain";
import type { EventLog } from "../state/eventLog";
import type { PlannerStore } from "../state/plannerStore";
import type { PersistedStore } from "../state/persistedStore";
import { setBusiness, setCatalog, setRules, usePerson, type SettingsState } from "../state/settingsState";
import { pressToast, toastFor, toastText, type UndoToast } from "../state/undoToast";
import { shareFile } from "../state/share";
import { backupFileName, makeBackup, parseBackup, settingsForThisPhone, type Backup, type BackupSummary } from "../state/backup";
import type { PlannerState } from "../state/plannerState";
import type { EventEnvelope } from "@hitl/core";
import { SyncBadge } from "../scan/parts";
import { formatDay, WRITE_OFF_LABEL } from "../scan/copy";
import { Sheet } from "../scan/sheets";

type Page =
  | { readonly kind: "root" }
  | { readonly kind: "pots" }
  | { readonly kind: "pot"; readonly id: string | null }
  | { readonly kind: "clients" }
  | { readonly kind: "client"; readonly id: string | null }
  | { readonly kind: "spot"; readonly clientId: string; readonly id: string | null }
  | { readonly kind: "rules" }
  | { readonly kind: "people" };

/** Everything on the phone that a restore replaces (records: this phone's own, not the seed). */
interface PhoneData {
  readonly settings: SettingsState;
  readonly planner: PlannerState;
  readonly events: readonly EventEnvelope[];
}

/** What a toast's button puts back: one settings change, or everything (a restore). */
type Restorable = { readonly kind: "settings"; readonly state: SettingsState } | { readonly kind: "all"; readonly data: PhoneData };

export interface SettingsFixture {
  readonly page?: Page;
  /** Retire this pot on open through the real save path, to review its toast. */
  readonly retire?: string;
}

const COLOURS = ["white", "cream", "pink", "red", "orange", "yellow", "green", "blue", "purple"];
const SIZES = ["small", "medium", "large"];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const newId = (prefix: string) => `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

export function SettingsScreen(props: {
  store: PersistedStore<SettingsState>;
  history: History;
  log: EventLog;
  planner: PlannerStore;
  onBack: () => void;
  onResetDemo?: () => void;
  fixture?: SettingsFixture;
}) {
  const { store, history, log } = props;
  const s = useSyncExternalStore(store.subscribe, store.get);
  const plannerState = useSyncExternalStore(props.planner.subscribe, props.planner.get);
  const syncState = useSyncExternalStore(log.subscribe, log.syncState);
  const pending = useSyncExternalStore(log.subscribe, log.pending);
  const [stack, setStack] = useState<Page[]>(props.fixture?.page ? [{ kind: "root" }, props.fixture.page] : [{ kind: "root" }]);
  const page = stack.at(-1)!;
  const go = (p: Page) => setStack((st) => [...st, p]);
  // Never pop below the root: a double tap (or a repeated call) must not empty the stack.
  const back = () => {
    if (stack.length <= 1) return props.onBack();
    setStack((st) => (st.length > 1 ? st.slice(0, -1) : st));
  };

  // Every change: say what happened, offer Undo (then Redo). Most changes touch settings only;
  // a restore replaces everything on the phone, so its Undo puts everything back.
  const [toast, setToast] = useState<UndoToast<Restorable> | null>(null);
  const act = (text: string, change: (s: SettingsState) => SettingsState) => {
    const before: Restorable = { kind: "settings", state: store.get() };
    store.update(change);
    setToast(toastFor(text, before, Date.now()));
  };
  /** Put `r` in place; returns what it replaced (so the toast can flip back). */
  const apply = (r: Restorable): Restorable => {
    if (r.kind === "settings") {
      const left = store.get();
      store.update(() => r.state);
      return { kind: "settings", state: left };
    }
    const left: PhoneData = { settings: store.get(), planner: props.planner.get(), events: log.replaceLocal(r.data.events) };
    store.update(() => r.data.settings);
    props.planner.update(() => r.data.planner);
    return { kind: "all", data: left };
  };

  // Restore: read the file, check it, then confirm with what's in it before replacing anything.
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [pendingRestore, setPendingRestore] = useState<{ backup: Backup; summary: BackupSummary } | null>(null);
  const pickBackup = async (file: File) => {
    const parsed = parseBackup(await file.text());
    if (!parsed.ok) return setRestoreError(parsed.error);
    setRestoreError(null);
    setPendingRestore({ backup: parsed.backup, summary: parsed.summary });
  };
  const restore = (backup: Backup) => {
    const settings = settingsForThisPhone(backup.settings, store.get());
    const before = apply({ kind: "all", data: { settings, planner: backup.planner, events: backup.events } });
    setPendingRestore(null);
    setToast(toastFor("Backup restored", before, Date.now()));
  };
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast((c) => (c?.id === toast.id ? null : c)), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  /** Apply a catalog edit; returns an error to show, or null and goes back. */
  const editCatalog = (text: string, result: EditResult): string | null => {
    if (!result.ok) return result.error;
    act(text, (st) => setCatalog(st, result.data));
    back();
    return null;
  };

  /** Save a pot. When circulation flips, say exactly that: it's the change she'll look for. */
  const savePot = (pot: Pot, isNew: boolean): string | null => {
    const before = s.catalog.pots.find((p) => p.id === pot.id);
    const text = isNew
      ? `Added ${pot.id.trim().toUpperCase()}`
      : before && before.active !== pot.active
        ? pot.active
          ? `${pot.id} back in circulation`
          : `${pot.id} retired`
        : `Saved ${pot.id}`;
    return editCatalog(text, isNew ? addPot(s.catalog, pot) : updatePot(s.catalog, pot));
  };

  // Dev fixture only: retire a pot through the real save path, so its toast can be reviewed.
  const fixtureRan = useRef(false);
  useEffect(() => {
    if (!import.meta.env.DEV || fixtureRan.current) return;
    fixtureRan.current = true;
    const id = props.fixture?.retire;
    const pot = id ? store.get().catalog.pots.find((p) => p.id === id) : undefined;
    if (pot) savePot({ ...pot, active: false }, false);
  }, []);

  const titles: Record<Page["kind"], string> = {
    root: "Settings",
    pots: "Pots",
    pot: page.kind === "pot" && page.id ? `Pot ${page.id}` : "New pot",
    clients: "Clients",
    client: page.kind === "client" && page.id ? (s.catalog.clients.find((c) => c.id === page.id)?.name ?? "Client") : "New client",
    spot: page.kind === "spot" && page.id ? (s.catalog.slots.find((x) => x.id === page.id)?.label ?? "Spot") : "New spot",
    rules: "Rules & defaults",
    people: "People & data",
  };
  const backLabel = stack.length > 1 ? titles[stack.at(-2)!.kind] : "Home";

  return (
    <main className="settings">
      {toast && (
        <div className="toast" role="status" key={toast.id}>
          <span>{toastText(toast)}</span>
          <button
            className="toast__undo"
            onClick={() => setToast(pressToast(toast, apply(toast.restoreTo), Date.now()).next)}
          >
            {toast.action === "undo" ? <Undo2 aria-hidden size={18} strokeWidth={2.5} /> : <Redo2 aria-hidden size={18} strokeWidth={2.5} />}
            {toast.action === "undo" ? "Undo" : "Redo"}
          </button>
        </div>
      )}
      <header className="plan__top">
        <button className="plan__back" onClick={back}>
          <ChevronLeft aria-hidden size={22} />
          {backLabel}
        </button>
      </header>
      <h1 className="settings__title">{titles[page.kind]}</h1>

      {page.kind === "root" && <Root s={s} history={history} syncState={syncState} pending={pending} go={go} />}
      {page.kind === "pots" && <PotsPage catalog={s.catalog} history={history} go={go} />}
      {page.kind === "pot" && (
        <PotPage
          key={page.id ?? "new"}
          pot={page.id ? s.catalog.pots.find((p) => p.id === page.id) : undefined}
          history={history}
          onSave={(pot, isNew) => savePot(pot, isNew)}
        />
      )}
      {page.kind === "clients" && <ClientsPage catalog={s.catalog} go={go} />}
      {page.kind === "client" && (
        <ClientPage
          key={page.id ?? "new"}
          catalog={s.catalog}
          client={page.id ? s.catalog.clients.find((c) => c.id === page.id) : undefined}
          go={go}
          onSave={(client, isNew) => editCatalog(isNew ? `Added ${client.name.trim()}` : `Saved ${client.name.trim()}`, isNew ? addClient(s.catalog, client) : updateClient(s.catalog, client))}
          onRemove={(id) => editCatalog(`Removed ${s.catalog.clients.find((c) => c.id === id)?.name ?? "client"}`, removeClient(s.catalog, id))}
        />
      )}
      {page.kind === "spot" && (
        <SpotPage
          key={page.id ?? "new"}
          clientName={s.catalog.clients.find((c) => c.id === page.clientId)?.name ?? ""}
          slot={page.id ? s.catalog.slots.find((x) => x.id === page.id) : undefined}
          onRoute={page.id ? plannerState.routes.find((r) => r.slotIds.includes(page.id!))?.name : undefined}
          onSave={(label, requirements) => {
            if (page.id) {
              const existing = s.catalog.slots.find((x) => x.id === page.id)!;
              return editCatalog(`Saved ${label.trim()}`, updateSlot(s.catalog, { ...existing, label, requirements }));
            }
            return editCatalog(`Added ${label.trim()}`, addSlot(s.catalog, { id: newId("spot"), clientId: page.clientId, label, requirements }));
          }}
          onRemove={() => editCatalog(`Removed the spot`, removeSlot(s.catalog, page.id!, history))}
        />
      )}
      {page.kind === "rules" && <RulesPage s={s} act={act} />}
      {page.kind === "people" && (
        <PeoplePage
          s={s}
          act={act}
          syncState={syncState}
          pending={pending}
          onBackup={async () => {
            const backup = makeBackup(s, props.planner.get(), log.all(), new Date());
            await shareFile(backupFileName(backup), JSON.stringify(backup, null, 2), "application/json");
          }}
          onPickBackup={(file) => void pickBackup(file)}
          restoreError={restoreError}
          {...(props.onResetDemo ? { onResetDemo: props.onResetDemo } : {})}
        />
      )}
      {pendingRestore && (
        <Sheet title="Restore this backup?" onClose={() => setPendingRestore(null)}>
          <div className="sheet__body">
            <p className="sheet__lead">
              {pendingRestore.summary.business}
              {pendingRestore.summary.exportedOn ? `, backed up ${formatDay(pendingRestore.summary.exportedOn)}` : ""}:{" "}
              {count(pendingRestore.summary.records, "record")}, {count(pendingRestore.summary.pots, "pot")}, {count(pendingRestore.summary.clients, "client")} and{" "}
              {count(pendingRestore.summary.routes, "route")}.
            </p>
            <p className="sheet__lead">It replaces everything on this phone. You can undo it straight after.</p>
            <button className="btn btn--light btn--block" onClick={() => restore(pendingRestore.backup)}>
              Restore
            </button>
          </div>
        </Sheet>
      )}
    </main>
  );
}

/* ── Root: the grouped list ───────────────────────────────────────────────── */

function Row(props: { label: string; detail?: string | undefined; onClick: () => void; alert?: boolean }) {
  return (
    <button className="grow" onClick={props.onClick}>
      <span className="grow__label">
        {props.label}
        {props.detail && (
          <span className={`grow__detail${props.alert ? " is-alert" : ""}`}>
            {props.alert && <TriangleAlert aria-hidden size={14} />}
            {props.detail}
          </span>
        )}
      </span>
      <ChevronRight aria-hidden size={18} />
    </button>
  );
}

function Root(props: { s: SettingsState; history: History; syncState: Parameters<typeof SyncBadge>[0]["state"]; pending: number; go: (p: Page) => void }) {
  const { s, go } = props;
  const missingCost = s.catalog.pots.filter((p) => p.asset?.costCents === undefined && !props.history.writtenOff(p.id)).length;
  return (
    <div className="settings__body">
      <h2 className="group__title">Your stock</h2>
      <div className="group">
        <Row label="Pots & asset register" detail={missingCost ? `${missingCost} without a cost` : count(s.catalog.pots.length, "pot")} alert={missingCost > 0} onClick={() => go({ kind: "pots" })} />
        <Row label="Clients, spots & requirements" detail={`${count(s.catalog.clients.length, "client")} · ${count(s.catalog.slots.length, "spot")}`} onClick={() => go({ kind: "clients" })} />
      </div>
      <h2 className="group__title">How the app works</h2>
      <div className="group">
        <Row label="Rules & defaults" detail={`${s.rules.windowMonths} months · records from ${formatDay(s.rules.historyKnownSince)}`} onClick={() => go({ kind: "rules" })} />
        <Row label="People & data" detail={`${s.people.current} · ${props.syncState === "saved" ? "all saved" : "saving"}`} onClick={() => go({ kind: "people" })} />
      </div>
    </div>
  );
}

/* ── Pots & asset register ───────────────────────────────────────────────── */

function PotsPage(props: { catalog: CatalogData; history: History; go: (p: Page) => void }) {
  const [q, setQ] = useState("");
  const collator = new Intl.Collator("en", { numeric: true });
  const pots = [...props.catalog.pots].filter((p) => p.id.toLowerCase().includes(q.trim().toLowerCase())).sort((a, b) => collator.compare(a.id, b.id));
  return (
    <div className="settings__body">
      <button className="btn btn--light btn--block" onClick={() => props.go({ kind: "pot", id: null })}>
        <Plus aria-hidden size={18} /> Add a pot
      </button>
      <label className="field settings__search">
        <span className="field__label">Search</span>
        <input className="field__input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pot number" />
      </label>
      <ul className="rlist">
        {pots.map((p) => {
          const off = props.history.writtenOff(p.id);
          return (
            <li key={p.id}>
              <button className={`rrow${off || !p.active ? " is-off" : ""}`} onClick={() => props.go({ kind: "pot", id: p.id })}>
                <span className="rrow__pot">{p.id}</span>
                <span className="rrow__main">
                  {cap([p.attributes.size?.[0], p.attributes.colour?.join(" and ")].filter(Boolean).join(" · ") || "No description")}
                  <small>{off ? `Written off ${formatDay(off.on)}` : !p.active ? "Retired" : p.asset?.boughtOn ? `Bought ${formatDay(p.asset.boughtOn)}` : "No purchase date"}</small>
                </span>
                <span className="rrow__side">{p.asset?.costCents !== undefined ? `R ${(p.asset.costCents / 100).toFixed(0)}` : <em>No cost</em>}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Pills(props: { options: readonly string[]; selected: readonly string[]; multi?: boolean; onChange: (v: string[]) => void; label: string }) {
  return (
    <div className="choices" role={props.multi ? "group" : "radiogroup"} aria-label={props.label}>
      {props.options.map((o) => {
        const on = props.selected.includes(o);
        return (
          <button
            key={o}
            role={props.multi ? "checkbox" : "radio"}
            aria-checked={on}
            className={`choice${on ? " is-picked" : ""}`}
            onClick={() => props.onChange(props.multi ? (on ? props.selected.filter((x) => x !== o) : [...props.selected, o]) : [o])}
          >
            {cap(o)}
          </button>
        );
      })}
    </div>
  );
}

function PotPage(props: { pot: Pot | undefined; history: History; onSave: (pot: Pot, isNew: boolean) => string | null }) {
  const p = props.pot;
  const [id, setId] = useState(p?.id ?? "");
  const [size, setSize] = useState<string[]>(p?.attributes.size ? [...p.attributes.size] : []);
  const [colours, setColours] = useState<string[]>(p?.attributes.colour ? [...p.attributes.colour] : []);
  const [boughtOn, setBoughtOn] = useState<string>(p?.asset?.boughtOn ?? "");
  const [cost, setCost] = useState<string>(p?.asset?.costCents !== undefined ? (p.asset.costCents / 100).toFixed(2) : "");
  const [supplier, setSupplier] = useState(p?.asset?.supplier ?? "");
  const [active, setActive] = useState(p?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const off = p ? props.history.writtenOff(p.id) : undefined;

  const save = () => {
    const costValue = cost.trim().replace(",", ".").replace(/\s|R/gi, "");
    if (costValue && !/^\d+(\.\d{1,2})?$/.test(costValue)) return setError("Type the cost as a number, like 520 or 520.50.");
    let bought: CalendarDate | undefined;
    try {
      bought = boughtOn ? calendarDate(boughtOn) : undefined;
    } catch {
      return setError("That purchase date isn't a real day.");
    }
    const asset = {
      ...(bought ? { boughtOn: bought } : {}),
      ...(costValue ? { costCents: Math.round(Number(costValue) * 100) } : {}),
      ...(supplier.trim() ? { supplier: supplier.trim() } : {}),
    };
    const pot: Pot = {
      id: p?.id ?? id,
      attributes: { ...(colours.length ? { colour: colours } : {}), ...(size.length ? { size } : {}) },
      active,
      ...(Object.keys(asset).length ? { asset } : {}),
    };
    setError(props.onSave(pot, !p));
  };

  return (
    <div className="settings__body">
      {off && (
        <p className="note">
          Written off {formatDay(off.on)} ({WRITE_OFF_LABEL[off.reason].toLowerCase()}). That record stays in Reports; the details below are still editable for your books.
        </p>
      )}
      {!p && (
        <label className="field">
          <span className="field__label">Number on the sticker</span>
          <input className="field__input field__input--big" value={id} onChange={(e) => setId(e.target.value.toUpperCase())} placeholder="e.g. E1" autoCapitalize="characters" maxLength={5} />
        </label>
      )}
      <p className="field__label settings__label">Size</p>
      <Pills label="Size" options={SIZES} selected={size} onChange={setSize} />
      <p className="field__label settings__label">Flower colours</p>
      <Pills label="Flower colours" options={COLOURS} selected={colours} multi onChange={setColours} />
      <h2 className="group__title">Asset register</h2>
      <label className="field">
        <span className="field__label">Bought on</span>
        <input className="field__input" type="date" value={boughtOn} onChange={(e) => setBoughtOn(e.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">Cost (R)</span>
        <input className="field__input" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 520" />
      </label>
      <label className="field">
        <span className="field__label">Supplier</span>
        <input className="field__input" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Where you bought it" />
      </label>
      {p && !off && (
        <button className="switchrow" role="switch" aria-checked={active} onClick={() => setActive(!active)}>
          <span>
            In circulation
            <small>{active ? "Can be planned and placed." : "Retired: kept in your records, never planned. Broken or lost? Write it off in Reports instead."}</small>
          </span>
          <span className={`switch${active ? " is-on" : ""}`} aria-hidden />
        </button>
      )}
      {error && <p className="field__hint">{error}</p>}
      <button className="btn btn--light btn--block" onClick={save}>
        {p ? "Save" : "Add pot"}
      </button>
    </div>
  );
}

/* ── Clients, spots & requirements ─────────────────────────────────────────────── */

function ClientsPage(props: { catalog: CatalogData; go: (p: Page) => void }) {
  const clients = [...props.catalog.clients].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="settings__body">
      <button className="btn btn--light btn--block" onClick={() => props.go({ kind: "client", id: null })}>
        <Plus aria-hidden size={18} /> Add a client
      </button>
      <div className="group settings__gap">
        {clients.map((c) => {
          const spots = props.catalog.slots.filter((s) => s.clientId === c.id).length;
          return (
            <Row
              key={c.id}
              label={c.name}
              detail={`${spots} spot${spots === 1 ? "" : "s"}${c.requirements.length ? ` · ${c.requirements.length} requirement${c.requirements.length === 1 ? "" : "s"}` : ""}`}
              onClick={() => props.go({ kind: "client", id: c.id })}
            />
          );
        })}
      </div>
    </div>
  );
}

function describeRequirement(r: Requirement): string {
  const noun = r.attribute === "colour" ? "flowers" : r.attribute === "size" ? "pots" : r.attribute;
  return r.rule === "only" ? `Only ${r.values.join(" or ")} ${noun}` : `No ${r.values.join(" or ")} ${noun}`;
}

function RequirementList(props: { requirements: readonly Requirement[]; onChange: (w: Requirement[]) => void }) {
  const [adding, setAdding] = useState(false);
  const [attribute, setAttribute] = useState<string[]>(["colour"]);
  const [rule, setRule] = useState<string[]>(["only"]);
  const [values, setValues] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const options = attribute[0] === "size" ? SIZES : COLOURS;
  const add = () => {
    const requirement: Requirement = { attribute: attribute[0] ?? "", rule: rule[0] === "not" ? "exclude" : "only", values };
    const problem = validRequirement(requirement);
    if (problem) return setError(problem);
    props.onChange([...props.requirements, requirement]);
    setAdding(false);
    setValues([]);
    setError(null);
  };
  return (
    <div className="requirements">
      {props.requirements.length === 0 && !adding && <p className="rtab__empty">No requirements.</p>}
      <ul className="rlist">
        {props.requirements.map((w, i) => (
          <li key={`${w.attribute}-${i}`} className="rrow rrow--static">
            <span className="rrow__main rrow__main--wide">{describeRequirement(w)}</span>
            <button className="iconbtn" aria-label={`Remove: ${describeRequirement(w)}`} onClick={() => props.onChange(props.requirements.filter((_, j) => j !== i))}>
              <X aria-hidden size={18} />
            </button>
          </li>
        ))}
      </ul>
      {adding ? (
        <div className="requirements__add">
          <Pills label="About" options={["colour", "size"]} selected={attribute} onChange={(v) => { setAttribute(v); setValues([]); }} />
          <Pills label="Rule" options={["only", "not"]} selected={rule} onChange={setRule} />
          <Pills label="Values" options={options} selected={values} multi onChange={setValues} />
          {error && <p className="field__hint">{error}</p>}
          <div className="requirements__actions">
            <button className="btn btn--ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
            <button className="btn btn--light" onClick={add}>
              Add requirement
            </button>
          </div>
        </div>
      ) : (
        <button className="btn btn--ghost btn--block" onClick={() => setAdding(true)}>
          <Plus aria-hidden size={18} /> Add a requirement
        </button>
      )}
    </div>
  );
}

function ClientPage(props: {
  catalog: CatalogData;
  client: Client | undefined;
  go: (p: Page) => void;
  onSave: (c: Client, isNew: boolean) => string | null;
  onRemove: (id: string) => string | null;
}) {
  const c = props.client;
  const [name, setName] = useState(c?.name ?? "");
  const [requirements, setRequirements] = useState<Requirement[]>(c ? [...c.requirements] : []);
  const [error, setError] = useState<string | null>(null);
  const spots = c ? props.catalog.slots.filter((s) => s.clientId === c.id) : [];
  return (
    <div className="settings__body">
      <label className="field">
        <span className="field__label">Client name</span>
        <input className="field__input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Smit Prokureurs" />
      </label>
      <h2 className="group__title">Requirements for every spot</h2>
      <RequirementList requirements={requirements} onChange={setRequirements} />
      {c && (
        <>
          <h2 className="group__title">Spots</h2>
          <div className="group">
            {spots.map((s) => (
              <Row
                key={s.id}
                label={s.label}
                {...(s.requirements.length ? { detail: s.requirements.map(describeRequirement).join(", ") } : {})}
                onClick={() => props.go({ kind: "spot", clientId: c.id, id: s.id })}
              />
            ))}
          </div>
          <button className="btn btn--ghost btn--block" onClick={() => props.go({ kind: "spot", clientId: c.id, id: null })}>
            <Plus aria-hidden size={18} /> Add a spot
          </button>
        </>
      )}
      {error && <p className="field__hint">{error}</p>}
      <button className="btn btn--light btn--block" onClick={() => setError(props.onSave({ id: c?.id ?? newId("client"), name, requirements }, !c))}>
        {c ? "Save" : "Add client"}
      </button>
      {c && spots.length === 0 && (
        <button className="btn btn--quiet btn--block" onClick={() => setError(props.onRemove(c.id))}>
          Remove this client
        </button>
      )}
      {!c && <p className="rtab__empty">Add the client first, then their spots.</p>}
    </div>
  );
}

function SpotPage(props: {
  clientName: string;
  slot: Slot | undefined;
  onRoute: string | undefined;
  onSave: (label: string, requirements: Requirement[]) => string | null;
  onRemove: () => string | null;
}) {
  const [label, setLabel] = useState(props.slot?.label ?? "");
  const [requirements, setRequirements] = useState<Requirement[]>(props.slot ? [...props.slot.requirements] : []);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="settings__body">
      <p className="rtab__intro settings__intro">At {props.clientName}</p>
      <label className="field">
        <span className="field__label">Spot name</span>
        <input className="field__input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Reception" />
      </label>
      <h2 className="group__title">Requirements for this spot</h2>
      <RequirementList requirements={requirements} onChange={setRequirements} />
      {error && <p className="field__hint">{error}</p>}
      <button className="btn btn--light btn--block" onClick={() => setError(props.onSave(label, requirements))}>
        {props.slot ? "Save" : "Add spot"}
      </button>
      {props.slot &&
        (props.onRoute ? (
          <p className="rtab__empty">This spot is on the route {props.onRoute}. Take it off the route before removing it.</p>
        ) : (
          <button className="btn btn--quiet btn--block" onClick={() => setError(props.onRemove())}>
            Remove this spot
          </button>
        ))}
    </div>
  );
}

/* ── Rules & defaults ─────────────────────────────────────────────────────── */

function Stepper(props: { label: string; value: number; unit: string; onChange: (n: number) => void; min: number; max: number }) {
  return (
    <div className="steprow">
      <span>{props.label}</span>
      <div className="stepper" role="group" aria-label={props.label}>
        <button aria-label={`Less: ${props.label}`} disabled={props.value <= props.min} onClick={() => props.onChange(props.value - 1)}>
          <Minus aria-hidden size={18} />
        </button>
        <span className="stepper__value">
          <b>{props.value}</b>
          <small>{props.unit}</small>
        </span>
        <button aria-label={`More: ${props.label}`} disabled={props.value >= props.max} onClick={() => props.onChange(props.value + 1)}>
          <Plus aria-hidden size={18} />
        </button>
      </div>
    </div>
  );
}

function RulesPage(props: { s: SettingsState; act: (text: string, change: (s: SettingsState) => SettingsState) => void }) {
  const { s, act } = props;
  const [since, setSince] = useState<string>(s.rules.historyKnownSince);
  // Follow the saved value, so Undo and Redo show here too.
  useEffect(() => setSince(s.rules.historyKnownSince), [s.rules.historyKnownSince]);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="settings__body">
      <Stepper
        label="No repeat at a client within"
        value={s.rules.windowMonths}
        unit="months"
        min={1}
        max={36}
        onChange={(n) => act(`Promise set to ${n} months`, (st) => setRules(st, { windowMonths: n }))}
      />
      <p className="rtab__empty">Your promise to clients. Every green and red is checked against it.</p>
      <label className="field settings__gap">
        <span className="field__label">Your records are complete from</span>
        <input
          className="field__input"
          type="date"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          onBlur={() => {
            if (since === s.rules.historyKnownSince) return;
            try {
              const day = calendarDate(since);
              setError(null);
              act(`Records from ${formatDay(day)}`, (st) => setRules(st, { historyKnownSince: day }));
            } catch {
              setError("That isn't a real day.");
            }
          }}
        />
      </label>
      {error && <p className="field__hint">{error}</p>}
      <p className="rtab__empty">Before this date the app can't confirm the full {s.rules.windowMonths} months, so it shows amber instead of green.</p>
      <Stepper
        label="Spare pots per day"
        value={s.rules.defaultSpares}
        unit="spares"
        min={0}
        max={10}
        onChange={(n) => act(`${n} spare${n === 1 ? "" : "s"} by default`, (st) => setRules(st, { defaultSpares: n }))}
      />
    </div>
  );
}

/* ── People & data ────────────────────────────────────────────────────────── */

function PeoplePage(props: {
  s: SettingsState;
  act: (text: string, change: (s: SettingsState) => SettingsState) => void;
  syncState: Parameters<typeof SyncBadge>[0]["state"];
  pending: number;
  onBackup: () => Promise<void>;
  onPickBackup: (file: File) => void;
  restoreError: string | null;
  onResetDemo?: () => void;
}) {
  const { s, act } = props;
  const filePicker = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [business, setBusinessName] = useState(s.business);
  useEffect(() => setBusinessName(s.business), [s.business]);
  const [confirmReset, setConfirmReset] = useState(false);
  return (
    <div className="settings__body">
      <h2 className="group__title">Who's using this phone</h2>
      <p className="rtab__empty">Their name goes on every record made here.</p>
      <div className="group settings__gap">
        {s.people.names.map((n) => (
          <button key={n} className="grow" role="radio" aria-checked={s.people.current === n} onClick={() => s.people.current !== n && act(`${n} is using this phone`, (st) => usePerson(st, n))}>
            <span className="grow__label">{n}</span>
            <span className={`radio${s.people.current === n ? " is-on" : ""}`} aria-hidden />
          </button>
        ))}
      </div>
      <div className="inline-add">
        <label className="field">
          <span className="field__label">Someone else</span>
          <input className="field__input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        </label>
        <button
          className="btn btn--ghost"
          disabled={!name.trim()}
          onClick={() => {
            act(`${name.trim()} is using this phone`, (st) => usePerson(st, name));
            setName("");
          }}
        >
          Add
        </button>
      </div>
      <h2 className="group__title">Your business</h2>
      <div className="inline-add">
        <label className="field">
          <span className="field__label">Business name (on exports)</span>
          <input className="field__input" value={business} onChange={(e) => setBusinessName(e.target.value)} />
        </label>
        <button className="btn btn--ghost" disabled={!business.trim() || business.trim() === s.business} onClick={() => act(`Business name saved`, (st) => setBusiness(st, business))}>
          Save
        </button>
      </div>
      <h2 className="group__title">Data</h2>
      <div className="group">
        <div className="grow grow--static">
          <span className="grow__label">Sync</span>
          <SyncBadge state={props.syncState} pending={props.pending} />
        </div>
      </div>
      <button className="btn btn--ghost btn--block" onClick={() => void props.onBackup()}>
        <Share aria-hidden size={18} /> Back up everything
      </button>
      <p className="rtab__empty">One file with every record, your pots, clients, routes and settings. Keep it somewhere safe until server sync arrives.</p>
      <button className="btn btn--ghost btn--block" onClick={() => filePicker.current?.click()}>
        <FileUp aria-hidden size={18} /> Restore from a backup
      </button>
      <input
        ref={filePicker}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared so choosing the same file again still fires.
          e.target.value = "";
          if (file) props.onPickBackup(file);
        }}
      />
      {props.restoreError && (
        <p className="field__hint" role="alert">
          {props.restoreError}
        </p>
      )}
      {props.onResetDemo && (
        <button className="btn btn--quiet btn--block" onClick={() => setConfirmReset(true)}>
          Reset the demo data
        </button>
      )}
      {confirmReset && props.onResetDemo && (
        <Sheet title="Reset the demo?" onClose={() => setConfirmReset(false)}>
          <div className="sheet__body">
            <p className="sheet__lead">Everything recorded in this demo, and every change to pots, clients, routes and settings, goes back to the start.</p>
            <button
              className="btn btn--danger btn--block"
              onClick={() => {
                setConfirmReset(false);
                props.onResetDemo?.();
              }}
            >
              Reset the demo
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
